import type { Chapter } from "../../types";
import type { BookAudioSettings } from "../../../shared/bookAudioSettings";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  WorkbenchPage,
  WorkbenchPageContent,
  WorkbenchPageHeader,
} from "../ui/workbench-page";
import { ChapterAudioControls } from "./ChapterAudioControls";

export function AudioPanel({
  activeChapter,
  chapters,
  cloudEnabled,
  bookId,
  audioSettings,
  onSelectChapter,
  onChapterVoiceChange,
  onListen,
}: {
  activeChapter: Chapter;
  chapters: Chapter[];
  cloudEnabled: boolean;
  bookId: string | undefined;
  audioSettings: BookAudioSettings;
  onSelectChapter: (chapterId: string) => void;
  onChapterVoiceChange: (chapterId: string, voiceId: string) => void;
  onListen?: () => void;
}) {
  const orderedChapters = [...chapters].sort(
    (a, b) => a.order - b.order || a.title.localeCompare(b.title)
  );

  return (
    <WorkbenchPage>
      <WorkbenchPageHeader
        title="Audio"
        description="Listen to chapters, assign voices, and generate audiobook audio away from the writing surface."
        actions={
          <Select value={activeChapter.id} onValueChange={onSelectChapter}>
            <SelectTrigger className="w-64 max-w-full" aria-label="Audio chapter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {orderedChapters.map((chapter) => (
                <SelectItem key={chapter.id} value={chapter.id}>
                  {chapter.order}. {chapter.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />
      <WorkbenchPageContent narrow>
        <div className="grid gap-4">
          <div>
            <h3 className="text-sm font-semibold text-foreground">
              Chapter {activeChapter.order}: {activeChapter.title}
            </h3>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              Audio is generated from the current saved manuscript text.
            </p>
          </div>
          <ChapterAudioControls
            activeChapter={activeChapter}
            chapters={chapters}
            cloudEnabled={cloudEnabled}
            bookId={bookId}
            audioSettings={audioSettings}
            onChapterVoiceChange={onChapterVoiceChange}
            onListen={onListen}
          />
        </div>
      </WorkbenchPageContent>
    </WorkbenchPage>
  );
}
